import { useLoadMore } from '@react-aria/utils';
import { cva, cx } from '@vizit/theme';
import * as React from 'react';
import {
  ListBox as AriaListBox,
  type ListBoxProps as AriaListBoxProps,
  Collection,
  ListBoxContext,
  composeRenderProps,
  useContextProps,
} from 'react-aria-components';
import { ListBoxItem } from './list-box.item';

const classes = cva({
  base: cx(
    'flex over shadow-md border border-primary',
    'overflow-x-hidden overflow-y-auto py-4 outline-none box-border',
    'data-[empty]:items-center data-[empty]:justify-center data-[empty]:italic data-[empty]:text-white',
  ),
  variants: {
    orientation: {
      horizontal: 'flex-row',
      vertical: 'flex-col',
    },
  },
  defaultVariants: {
    orientation: 'vertical',
  },
});

interface ListBoxProps<T extends object> extends AriaListBoxProps<T> {
  hasMore?: boolean;
  isLoading?: boolean;
  /**
   * Handler that is called when the list box is scrolled to the bottom. Used for infinite scrolling.
   *
   * @optional
   * @type {() => void}
   */
  onLoadMore?: () => void;
}

function ListBox<T extends object>(_props: ListBoxProps<T>, forwardedRef: React.ForwardedRef<HTMLDivElement>) {
  const [props, ref] = useContextProps(_props, forwardedRef, ListBoxContext);

  const {
    children,
    className: classNameProp,
    hasMore,
    isLoading,
    onLoadMore,
    items,
    renderEmptyState,
    ...other
  } = props;

  const handleLoadMore = React.useCallback(() => {
    onLoadMore?.();
  }, [onLoadMore]);

  useLoadMore({ isLoading, onLoadMore: handleLoadMore }, ref);

  const renderContent = (): React.ReactNode => {
    if (!items) {
      // If children is a React element or array of React elements, return it directly
      if (React.isValidElement(children) || Array.isArray(children)) {
        return children;
      }

      // If children is a function but no items, return null
      if (typeof children === 'function') {
        return null;
      }

      return children as React.ReactNode;
    }

    // If children is not a function but we have items, wrap in Collection
    if (typeof children !== 'function') {
      return <Collection items={items}>{() => children as React.ReactNode}</Collection>;
    }

    // If children is a function and we have items, use it with Collection
    return (
      <Collection items={items}>
        {(item: T) => {
          const renderedChild = (children as (item: T) => React.ReactNode)(item);
          return renderedChild;
        }}
      </Collection>
    );
  };

  return (
    <AriaListBox
      {...other}
      ref={ref}
      className={composeRenderProps(classNameProp, (className) =>
        // The border is handled by the PopOver component
        cx('vzt-list-box border-none', classes({ ...props, className })),
      )}
      items={items}
      renderEmptyState={renderEmptyState}
    >
      {renderContent()}
      {isLoading &&
        Array.from({ length: 5 }).map((_, index) => (
          <ListBoxItem
            key={`list_item_${index.toString()}`}
            id={`list_item_${index.toString()}`}
            isLoading={isLoading}
            textValue="Loading..."
          />
        ))}
    </AriaListBox>
  );
}

const _ListBox = React.forwardRef(ListBox) as <T extends object>(
  props: ListBoxProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> },
) => ReturnType<typeof ListBox>;

export type { ListBoxProps };
export { _ListBox as ListBox };
